import React, { useState, useEffect } from 'react';
import { postAuth, get, uploadFile } from '../data/api';
import { BUSINESS_CATEGORIES } from '../data/japan_life';
import { POPULAR_CITIES } from '../data/japan_locations';
import { POPULAR_KENYA_COUNTIES, KENYA_CONNECT_CATEGORIES } from '../data/kenya_connect';

function SubmitShell({ title, subtitle, onClose, children, onSubmit, loading, error, success }) {
  return (
    <div className="comm-modal-backdrop" onClick={onClose}>
      <div className="comm-modal comm-modal-wide" onClick={e => e.stopPropagation()}>
        <h2>{title}</h2>
        <p className="comm-modal-sub">{subtitle}</p>
        {success ? (
          <div className="comm-success">
            <p>{success}</p>
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form onSubmit={onSubmit}>{children}</form>
        )}
        {error && !success && <p className="text-red text-sm" style={{ marginTop: 10 }}>{error}</p>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="comm-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

export function BusinessSubmitModal({ prefecture, city, onClose, onDone }) {
  const [form, setForm] = useState({
    name: '', category: 'Restaurant', description: '',
    japan_prefecture: prefecture, japan_city: city || '',
    phone: '', email: '', website: '', address: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = await postAuth('businesses', { action: 'create' }, form);
      if (!data.ok) { setError(data.error || 'Failed'); return; }
      setSuccess(data.message || 'Business submitted for approval.');
      onDone?.();
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  return (
    <SubmitShell title="List a business" subtitle="Pending admin approval before going live."
      onClose={onClose} onSubmit={submit} loading={loading} error={error} success={success}>
      <Field label="Business name *"><input required value={form.name} onChange={e => set('name')(e.target.value)} /></Field>
      <Field label="Category *">
        <select value={form.category} onChange={e => set('category')(e.target.value)}>
          {BUSINESS_CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Description *"><textarea required value={form.description} onChange={e => set('description')(e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Prefecture *"><input required value={form.japan_prefecture} onChange={e => set('japan_prefecture')(e.target.value)} /></Field>
        <Field label="City">
          <input list="biz-cities" value={form.japan_city} onChange={e => set('japan_city')(e.target.value)} />
          <datalist id="biz-cities">{(POPULAR_CITIES[form.japan_prefecture] || []).map(c => <option key={c} value={c} />)}</datalist>
        </Field>
      </div>
      <Field label="Address"><input value={form.address} onChange={e => set('address')(e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Phone"><input value={form.phone} onChange={e => set('phone')(e.target.value)} /></Field>
        <Field label="Email"><input type="email" value={form.email} onChange={e => set('email')(e.target.value)} /></Field>
      </div>
      <Field label="Website"><input value={form.website} onChange={e => set('website')(e.target.value)} placeholder="https://" /></Field>
      <div className="comm-modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Submitting…' : 'Submit listing'}</button>
      </div>
    </SubmitShell>
  );
}

export function EventSubmitModal({ prefecture, city, onClose, onDone }) {
  const [form, setForm] = useState({
    title: '', description: '', japan_prefecture: prefecture, japan_city: city || '',
    venue: '', event_date: '', event_time: '', is_free: 1, ticket_price: 0, max_attendees: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const body = { ...form, is_free: form.is_free ? 1 : 0, max_attendees: form.max_attendees || null };
      const data = await postAuth('events', { action: 'create' }, body);
      if (!data.ok) { setError(data.error || 'Failed'); return; }
      setSuccess(data.message || 'Event submitted for approval.');
      onDone?.();
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  return (
    <SubmitShell title="Post an event" subtitle="Community events, meetups, fellowships — pending approval."
      onClose={onClose} onSubmit={submit} loading={loading} error={error} success={success}>
      <Field label="Event title *"><input required value={form.title} onChange={e => set('title')(e.target.value)} /></Field>
      <Field label="Description *"><textarea required value={form.description} onChange={e => set('description')(e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Date *"><input type="date" required value={form.event_date} onChange={e => set('event_date')(e.target.value)} /></Field>
        <Field label="Time"><input type="time" value={form.event_time} onChange={e => set('event_time')(e.target.value)} /></Field>
      </div>
      <Field label="Venue"><input value={form.venue} onChange={e => set('venue')(e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Prefecture *"><input required value={form.japan_prefecture} onChange={e => set('japan_prefecture')(e.target.value)} /></Field>
        <Field label="City"><input value={form.japan_city} onChange={e => set('japan_city')(e.target.value)} /></Field>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 14 }}>
        <input type="checkbox" checked={!!form.is_free} onChange={e => set('is_free')(e.target.checked ? 1 : 0)} />
        Free event
      </label>
      {!form.is_free && (
        <Field label="Ticket price (JPY)"><input type="number" value={form.ticket_price} onChange={e => set('ticket_price')(e.target.value)} /></Field>
      )}
      <Field label="Max attendees"><input type="number" value={form.max_attendees} onChange={e => set('max_attendees')(e.target.value)} placeholder="Leave blank for unlimited" /></Field>
      <div className="comm-modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Submitting…' : 'Submit event'}</button>
      </div>
    </SubmitShell>
  );
}

export function JobSubmitModal({ prefecture, city, onClose, onDone }) {
  const [form, setForm] = useState({
    title: '', company: '', type: 'baito', description: '', requirements: '',
    japan_prefecture: prefecture, japan_city: city || '', salary: '',
    apply_url: '', apply_email: '', tags: '', visa_support: false, english_ok: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = await postAuth('jobs', { action: 'create' }, form);
      if (!data.ok) { setError(data.error || 'Failed'); return; }
      setSuccess(data.message || 'Job submitted for approval.');
      onDone?.();
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  return (
    <SubmitShell title="Post a job" subtitle="Full-time or baito (part-time) — pending approval."
      onClose={onClose} onSubmit={submit} loading={loading} error={error} success={success}>
      <Field label="Job title *"><input required value={form.title} onChange={e => set('title')(e.target.value)} /></Field>
      <Field label="Company *"><input required value={form.company} onChange={e => set('company')(e.target.value)} /></Field>
      <Field label="Type *">
        <select value={form.type} onChange={e => set('type')(e.target.value)}>
          <option value="fulltime">Full-time</option>
          <option value="baito">Part-time (baito)</option>
        </select>
      </Field>
      <Field label="Description *"><textarea required value={form.description} onChange={e => set('description')(e.target.value)} /></Field>
      <Field label="Requirements"><textarea value={form.requirements} onChange={e => set('requirements')(e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Prefecture *"><input required value={form.japan_prefecture} onChange={e => set('japan_prefecture')(e.target.value)} /></Field>
        <Field label="City"><input value={form.japan_city} onChange={e => set('japan_city')(e.target.value)} /></Field>
      </div>
      <Field label="Salary"><input value={form.salary} onChange={e => set('salary')(e.target.value)} placeholder="¥1,200/hr or ¥3M/year" /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Apply URL"><input value={form.apply_url} onChange={e => set('apply_url')(e.target.value)} /></Field>
        <Field label="Apply email"><input type="email" value={form.apply_email} onChange={e => set('apply_email')(e.target.value)} /></Field>
      </div>
      <Field label="Tags (comma-separated)"><input value={form.tags} onChange={e => set('tags')(e.target.value)} placeholder="English OK, weekend" /></Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 14 }}>
        <input type="checkbox" checked={form.visa_support} onChange={e => set('visa_support')(e.target.checked)} /> Visa support available
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 14 }}>
        <input type="checkbox" checked={form.english_ok} onChange={e => set('english_ok')(e.target.checked)} /> English OK
      </label>
      <div className="comm-modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Submitting…' : 'Submit job'}</button>
      </div>
    </SubmitShell>
  );
}

export function ServiceProviderModal({ serviceSlug, serviceName, onClose, onDone }) {
  const [form, setForm] = useState({ service_slug: serviceSlug, headline: '', detail: '', rate: '', availability: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = await postAuth('service_providers', { action: 'create' }, form);
      if (!data.ok) { setError(data.error || 'Failed'); return; }
      setSuccess(data.message || 'Listing submitted for approval.');
      onDone?.();
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  return (
    <SubmitShell title={`Offer ${serviceName} help`} subtitle="List yourself as a community service provider."
      onClose={onClose} onSubmit={submit} loading={loading} error={error} success={success}>
      <Field label="Headline *"><input required value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))} placeholder="e.g. Visa renewal help in Tokyo" /></Field>
      <Field label="Details"><textarea value={form.detail} onChange={e => setForm(f => ({ ...f, detail: e.target.value }))} /></Field>
      <Field label="Rate"><input value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} placeholder="¥5,000 / consultation" /></Field>
      <div className="comm-modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Submitting…' : 'Submit'}</button>
      </div>
    </SubmitShell>
  );
}

export function AlbumSubmitModal({ prefecture, city, onClose, onDone }) {
  const [form, setForm] = useState({
    title: '', japan_prefecture: prefecture, japan_city: city || '',
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [albumId, setAlbumId] = useState(null);

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setError('');
    try {
      for (const file of files) {
        const data = await uploadFile(file);
        if (!data.ok) { setError(data.error || 'Upload failed'); break; }
        setPhotos(prev => [...prev, { url: data.url, caption: '' }]);
      }
    } catch { setError('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  }

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      let id = albumId;
      if (!id) {
        const created = await postAuth('albums', { action: 'create' }, form);
        if (!created.ok) { setError(created.error || 'Failed'); return; }
        id = created.id;
        setAlbumId(id);
      }
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i];
        await postAuth('albums', { action: 'add_photo' }, {
          album_id: id, url: p.url, caption: p.caption || null, sort_order: i,
        });
      }
      setSuccess('Album submitted with photos — pending admin approval.');
      onDone?.();
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  return (
    <SubmitShell title="Share an album" subtitle="Event photos, meetups, community moments — pending approval."
      onClose={onClose} onSubmit={submit} loading={loading} error={error} success={success}>
      <Field label="Album title *"><input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Prefecture *"><input required value={form.japan_prefecture} onChange={e => setForm(f => ({ ...f, japan_prefecture: e.target.value }))} /></Field>
        <Field label="City"><input value={form.japan_city} onChange={e => setForm(f => ({ ...f, japan_city: e.target.value }))} /></Field>
      </div>
      <Field label="Photos">
        <input type="file" accept="image/*" multiple onChange={handleFiles} disabled={uploading} />
        {uploading && <p className="text-sm text-muted">Uploading…</p>}
        {photos.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {photos.map((p, i) => (
              <img key={i} src={p.url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }} />
            ))}
          </div>
        )}
      </Field>
      <div className="comm-modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading || uploading || !photos.length}>
          {loading ? 'Submitting…' : 'Submit album'}
        </button>
      </div>
    </SubmitShell>
  );
}

export function KenyaListingSubmitModal({ type, onClose, onDone }) {
  const typeLabel = KENYA_CONNECT_CATEGORIES.find(c => c.id === type)?.name || type;
  const [form, setForm] = useState({
    type, title: '', description: '', kenya_county: 'Nairobi', kenya_area: '',
    price: '', price_currency: 'KES', size_info: '',
    contact_phone: '', contact_email: '', image_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  async function handleImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadFile(file);
      if (data.ok) set('image_url')(data.url);
    } catch { setError('Image upload failed'); }
  }

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = await postAuth('kenya_listings', { action: 'create' }, form);
      if (!data.ok) { setError(data.error || 'Failed'); return; }
      setSuccess(data.message || 'Listing submitted for approval.');
      onDone?.();
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  return (
    <SubmitShell title={`List ${typeLabel}`} subtitle="Pending admin approval before going live."
      onClose={onClose} onSubmit={submit} loading={loading} error={error} success={success}>
      <Field label="Title *"><input required value={form.title} onChange={e => set('title')(e.target.value)} /></Field>
      <Field label="Description *"><textarea required value={form.description} onChange={e => set('description')(e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="County *">
          <select value={form.kenya_county} onChange={e => set('kenya_county')(e.target.value)}>
            {POPULAR_KENYA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Area / Town"><input value={form.kenya_area} onChange={e => set('kenya_area')(e.target.value)} /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Price"><input value={form.price} onChange={e => set('price')(e.target.value)} placeholder="e.g. 2,500,000" /></Field>
        <Field label="Size / Details"><input value={form.size_info} onChange={e => set('size_info')(e.target.value)} placeholder="e.g. 0.5 acres" /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Contact phone"><input value={form.contact_phone} onChange={e => set('contact_phone')(e.target.value)} /></Field>
        <Field label="Contact email"><input type="email" value={form.contact_email} onChange={e => set('contact_email')(e.target.value)} /></Field>
      </div>
      <Field label="Photo"><input type="file" accept="image/*" onChange={handleImage} /></Field>
      {form.image_url && <img src={form.image_url} alt="" style={{ maxWidth: 120, borderRadius: 8, marginBottom: 12 }} />}
      <div className="comm-modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Submitting…' : 'Submit listing'}</button>
      </div>
    </SubmitShell>
  );
}

export function FeaturedRequestModal({ entityType, entityId, entityLabel, onClose }) {
  const [method, setMethod] = useState('bank');
  const [ref, setRef] = useState('');
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    get('featured', { action: 'pricing' }).then(d => setPricing(d.pricing?.[entityType])).catch(() => {});
  }, [entityType]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = await postAuth('featured', { action: 'request' }, {
        entity_type: entityType, entity_id: entityId, payment_method: method, payment_ref: ref,
      });
      if (!data.ok) { setError(data.error || 'Failed'); return; }
      setSuccess(data.message || 'Request submitted.');
    } catch { setError('Network error'); }
    finally { setLoading(false); }
  }

  if (success) {
    return (
      <div className="comm-modal-backdrop" onClick={onClose}>
        <div className="comm-modal" onClick={e => e.stopPropagation()}>
          <h2>Featured request sent</h2>
          <p className="comm-modal-sub">{success}</p>
          {pricing && <p className="text-sm">Amount: {pricing.currency} {pricing.amount?.toLocaleString()} for {pricing.days} days</p>}
          <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="comm-modal-backdrop" onClick={onClose}>
      <div className="comm-modal" onClick={e => e.stopPropagation()}>
        <h2>Feature listing</h2>
        <p className="comm-modal-sub">{entityLabel}</p>
        {pricing && (
          <p className="text-sm" style={{ marginBottom: 12 }}>
            {pricing.currency} {pricing.amount?.toLocaleString()} / {pricing.days} days — pay then we activate within 24h.
          </p>
        )}
        <form onSubmit={submit}>
          <div className="comm-field">
            <label>Payment method</label>
            <select value={method} onChange={e => setMethod(e.target.value)}>
              <option value="bank">Bank transfer</option>
              <option value="mpesa">M-Pesa</option>
            </select>
          </div>
          <div className="comm-field">
            <label>Payment reference (optional)</label>
            <input value={ref} onChange={e => setRef(e.target.value)} placeholder="Transaction ID" />
          </div>
          {error && <p className="text-red text-sm">{error}</p>}
          <div className="comm-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Submitting…' : 'Request featured'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
